import { useCallback, useEffect, useRef, useState } from 'react';

import { css, cssMap } from '@compiled/react';

import { Image } from '@/libraries/design/components/image/src/image';

import teamworkCollectionVideoForeground from './assets/video-foreground-teamwork-collection.svg';
import { type ChoiceValue } from './types';

/**
 * Loom presenter clip played on the panel illustration's video "screen". Same
 * clip as Loom's crossflow confirmation modal (loomhq/loom#71266), served from
 * the Orange Logic DAM CDN like other WAC videos.
 */
export const OPT_IN_PREVIEW_VIDEO_SRC =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/ipar5pr23bo2u04lfugytxn27snq82gn.mp4';

/** How long the clip plays before handing back to the static illustration. */
export const OPT_IN_PREVIEW_VIDEO_DURATION_MS = 5000;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
// The graphic panel is hidden below 768px, so skip the download there.
const NARROW_VIEWPORT_QUERY = '(max-width: 767px)';

type PlaybackStatus = 'off' | 'loading' | 'playing';

const videoCss = css({
	position: 'absolute',
	objectFit: 'cover',
	// Matches the illustration's tilted screen; rotated around its top-left corner.
	transform: 'rotate(-2.24deg)',
	transformOrigin: 'top left',
	pointerEvents: 'none',
	// Stay invisible until frames are actually playing so the static screen
	// never flashes blank while the clip loads.
	opacity: 0,
	transition: 'opacity 200ms ease-out',
});

/**
 * Screen rect of each illustration, as percentages of the 484x601 panel
 * artboard (see `opt-in-graphic-panel`). Teamwork Collection is the Figma
 * screen rect (x 147.246, y 149.221, 198.694 x 144.362, r 2.018); Loom-only is
 * the same screen scaled 1.137x (x 135.11, y 173.98, 225.94 x 164.13).
 */
const videoScreenCss = cssMap({
	'teamwork-collection': {
		insetBlockStart: '24.8288%',
		insetInlineStart: '30.4227%',
		width: '41.0525%',
		height: '24.0203%',
		borderRadius: '1.0158% / 1.3981%',
	},
	'loom-only': {
		insetBlockStart: '28.9484%',
		insetInlineStart: '27.9153%',
		width: '46.6818%',
		height: '27.3095%',
		borderRadius: '1.0158% / 1.3983%',
	},
});

const foregroundCss = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	width: '100%',
	height: '100%',
	pointerEvents: 'none',
	opacity: 0,
	transition: 'opacity 200ms ease-out',
});

const visibleCss = css({
	opacity: 1,
});

/**
 * Artwork that overlaps an illustration's screen and must stay above the
 * video (Teamwork Collection's "Summarize meeting" pill and cursor). Loom-only
 * has nothing overlapping its screen.
 */
const VIDEO_FOREGROUNDS: Partial<Record<ChoiceValue, string>> = {
	'teamwork-collection': teamworkCollectionVideoForeground,
};

export type OptInPreviewVideoProps = {
	/** Illustration currently shown; positions the video on its screen. */
	illustration: ChoiceValue;
	/** Clip to play. Defaults to {@link OPT_IN_PREVIEW_VIDEO_SRC}. */
	src?: string;
};

/**
 * Plays the Loom clip over the panel illustration's screen for 5 seconds, then
 * removes itself so the static illustration shows again.
 *
 * Each illustration's clip plays at most once per modal visit: the initial card
 * plays on load, and another card plays the first time it's selected (Variant
 * A's "Loom only"). Returning to a card that already played keeps it static.
 *
 * Client-only (decided in an effect, so SSR markup never includes it), and
 * skipped for reduced motion and narrow viewports. A clip also stops early if
 * reduced motion turns on, playback ends/fails/is blocked, or the user switches
 * cards mid-play.
 */
export const OptInPreviewVideo = ({
	illustration,
	src = OPT_IN_PREVIEW_VIDEO_SRC,
}: OptInPreviewVideoProps) => {
	const [playedIllustrations, setPlayedIllustrations] = useState<ChoiceValue[]>([illustration]);
	const [playbackIllustration, setPlaybackIllustration] = useState<ChoiceValue | null>(
		illustration,
	);

	useEffect(() => {
		if (playedIllustrations.includes(illustration)) {
			// Already had its turn (or is the one playing now): stay or go static.
			if (playbackIllustration !== illustration) {
				setPlaybackIllustration(null);
			}
			return;
		}
		setPlayedIllustrations([...playedIllustrations, illustration]);
		setPlaybackIllustration(illustration);
	}, [illustration, playedIllustrations, playbackIllustration]);

	// Rendering only while the selection matches unmounts a clip as soon as the
	// user switches away; keying by illustration gives each card a fresh player.
	if (playbackIllustration !== illustration) {
		return null;
	}

	return <PreviewVideoPlayback key={illustration} illustration={illustration} src={src} />;
};

type PreviewVideoPlaybackProps = {
	illustration: ChoiceValue;
	src: string;
};

/** One playback of the clip on one illustration's screen. */
const PreviewVideoPlayback = ({ illustration, src }: PreviewVideoPlaybackProps) => {
	const [status, setStatus] = useState<PlaybackStatus>('off');
	const videoRef = useRef<HTMLVideoElement>(null);
	const stopTimeoutRef = useRef<number | null>(null);

	const clearStopTimeout = useCallback(() => {
		if (stopTimeoutRef.current !== null) {
			window.clearTimeout(stopTimeoutRef.current);
			stopTimeoutRef.current = null;
		}
	}, []);

	const stop = useCallback(() => {
		clearStopTimeout();
		setStatus('off');
	}, [clearStopTimeout]);

	useEffect(() => {
		if (!src || typeof window.matchMedia !== 'function') {
			return undefined;
		}

		const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
		if (reducedMotion.matches || window.matchMedia(NARROW_VIEWPORT_QUERY).matches) {
			return undefined;
		}

		setStatus('loading');

		const stopForReducedMotion = (event: MediaQueryListEvent) => {
			if (event.matches) {
				stop();
			}
		};
		reducedMotion.addEventListener?.('change', stopForReducedMotion);

		return () => {
			clearStopTimeout();
			reducedMotion.removeEventListener?.('change', stopForReducedMotion);
		};
	}, [src, stop, clearStopTimeout]);

	// Start playback explicitly so a blocked autoplay (rejected promise) falls
	// back to the static illustration instead of leaving a frozen first frame.
	useEffect(() => {
		if (status !== 'loading') {
			return;
		}
		const playPromise: Promise<void> | undefined = videoRef.current?.play();
		playPromise?.catch(stop);
	}, [status, stop]);

	const handlePlaying = () => {
		setStatus('playing');
		// `playing` also fires after buffering stalls; only start the clock once.
		if (stopTimeoutRef.current === null) {
			stopTimeoutRef.current = window.setTimeout(stop, OPT_IN_PREVIEW_VIDEO_DURATION_MS);
		}
	};

	if (status === 'off') {
		return null;
	}

	const isPlaying = status === 'playing';
	const foreground = VIDEO_FOREGROUNDS[illustration];

	return (
		<>
			<video
				ref={videoRef}
				css={[videoCss, videoScreenCss[illustration], isPlaying && visibleCss]}
				src={src}
				muted
				playsInline
				preload="auto"
				aria-hidden="true"
				tabIndex={-1}
				onPlaying={handlePlaying}
				onEnded={stop}
				onError={stop}
				data-testid="loom-land-opt-in-preview-video"
				data-playing={isPlaying}
			/>
			{foreground && (
				<Image
					css={[foregroundCss, isPlaying && visibleCss]}
					src={foreground}
					alt=""
					aria-hidden="true"
					loading="eager"
					data-testid="loom-land-opt-in-preview-video-foreground"
				/>
			)}
		</>
	);
};
